import dynamic from 'next/dynamic';
import { MouseEvent, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsMapViewed from '@alltrails/amplitude/events/logTrailDetailsMapViewed';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import useUser from '@alltrails/context/hooks/useUser';
import Image from '@alltrails/core/components/Image';
import getStaticMapUrl from '@alltrails/core/utils/getStaticMapUrl';
import IconButton from '@alltrails/denali/components/IconButton';
import Expand from '@alltrails/denali/icons/Expand';
import { BREAKPOINT_MD_MARGIN, BREAKPOINT_MD_SIZE, BREAKPOINT_SM_SIZE } from '@alltrails/denali/tokens';
import useIsTrailMapOpen from '@/hooks/useIsTrailMapOpen';
import styles from './StaticMap.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

const messages = defineMessages({
  ALT_EXPAND: { defaultMessage: 'Expand map' }
});

type StaticMapProps = {
  mapId: number;
  trailName: string;
  isMobile: boolean;
  isCard?: boolean;
  isScrollLimitActive?: boolean;
  trailId: number;
  trailPageType: TrailPageType;
};

const StaticMap = ({ mapId, trailName, isMobile, isCard, trailId, trailPageType }: StaticMapProps) => {
  const intl = useIntl();
  const user = useUser();
  const { openTrailMap } = useIsTrailMapOpen();
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const publicBaseUrl = usePublicBaseUrl();

  const cardMapSize = isMobile ? '320x320' : '1280x582';
  const staticMapUrl = getStaticMapUrl({ baseUrl: publicBaseUrl, id: mapId, scale: 2, size: isCard ? cardMapSize : '160x160', type: 'maps' });

  useEffect(() => {
    logTrailDetailsMapViewed({ trail_id: trailId });
  }, [trailId]);

  const logStaticMapClicked = () => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: 'view map',
      trail_page_location: isCard ? 'map card' : 'hero image inset',
      trail_page_type: trailPageType
    });
  };

  const sizes = isCard
    ? `(max-width: ${BREAKPOINT_MD_SIZE + BREAKPOINT_MD_MARGIN}px 1165px), (max-width: ${BREAKPOINT_SM_SIZE}px) 688px`
    : `(max-width: ${BREAKPOINT_SM_SIZE}px) 70px, 120px`;

  const staticImage = (
    <Image
      alt={intl.formatMessage({ defaultMessage: '{name} map' }, { name: trailName })}
      className={styles.staticMap}
      fill
      loading="eager"
      sizes={sizes}
      src={staticMapUrl}
    />
  );

  const onTrailMapClick = (e: MouseEvent) => {
    e.stopPropagation();
    logStaticMapClicked();
    if (user) {
      openTrailMap();
    } else {
      setIsSignupOpen(true);
    }
  };

  return (
    <div className={isCard ? styles.staticMapCard : styles.staticMapContainer}>
      <button className={styles.button} onClick={onTrailMapClick} title={intl.formatMessage(messages.ALT_EXPAND)}>
        {staticImage}
      </button>
      {isCard && (
        <IconButton
          className={styles.expandButton}
          icon={{ Component: Expand }}
          onClick={onTrailMapClick}
          stopPropagation
          title={intl.formatMessage(messages.ALT_EXPAND)}
          variant="elevated"
        />
      )}
      {isSignupOpen && (
        <SignUpModal
          isOpen={isSignupOpen}
          closeModal={() => setIsSignupOpen(false)}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          onSuccess={() => setIsSignupOpen(false)}
          trigger="View Static Map"
          upsellTrigger="View Static Map"
        />
      )}
    </div>
  );
};

export default StaticMap;
